import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { pb, fileUrl } from '@/lib/pb';
import { COLLECTIONS, type Post, type Category, type PostStatus } from '@/lib/types';
import { slugify } from '@/lib/slug';
import { Btn, Icon } from '@/ui';
import { useAuth } from './AuthContext';
import { PageHeader, Card, Spinner, TextField, SelectField, useToast, pbError } from './ui';
import { ImageDropzone } from './blog/ImageDropzone';
import { RichTextEditor } from './blog/RichTextEditor';
import { SerpPreview } from './blog/SerpPreview';

const SUMMARY_MAX = 500;
const SEO_TITLE_REC = 60;
const SEO_DESC_REC = 155;

export function BlogEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = React.useState(!isNew);
  const [saving, setSaving] = React.useState(false);
  const [cats, setCats] = React.useState<Category[]>([]);
  const [record, setRecord] = React.useState<Post | null>(null);

  // form
  const [title, setTitle] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [summary, setSummary] = React.useState('');
  const [content, setContent] = React.useState('');
  const [status, setStatus] = React.useState<PostStatus>('draft');
  const [categoryId, setCategoryId] = React.useState('');
  const [seoTitle, setSeoTitle] = React.useState('');
  const [seoDescription, setSeoDescription] = React.useState('');
  const [seoKeyword, setSeoKeyword] = React.useState('');
  const [file, setFile] = React.useState<File | null>(null);
  const [removeImage, setRemoveImage] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    const init = async () => {
      try {
        const catList = await pb.collection(COLLECTIONS.categories).getFullList<Category>({ sort: 'name' });
        if (alive) setCats(catList);
        if (id) {
          const p = await pb.collection(COLLECTIONS.posts).getOne<Post>(id);
          if (!alive) return;
          setRecord(p);
          setTitle(p.title); setSlug(p.slug); setSlugTouched(true);
          setSummary(p.summary); setContent(p.content); setStatus(p.status);
          setCategoryId(p.category); setSeoTitle(p.seo_title);
          setSeoDescription(p.seo_description); setSeoKeyword(p.seo_keyword);
        }
      } catch (e) {
        toast.show(pbError(e), 'error');
      } finally {
        if (alive) setLoading(false);
      }
    };
    void init();
    return () => { alive = false; };
  }, [id, toast]);

  const onTitle = (v: string) => {
    setTitle(v);
    if (!slugTouched) setSlug(slugify(v));
  };

  const addCategory = async () => {
    const name = window.prompt('Name der neuen Kategorie:');
    if (!name?.trim()) return;
    try {
      const c = await pb.collection(COLLECTIONS.categories).create<Category>({ name: name.trim(), slug: slugify(name) });
      setCats((prev) => [...prev, c].sort((a, b) => a.name.localeCompare(b.name)));
      setCategoryId(c.id);
      toast.show('Kategorie angelegt.', 'success');
    } catch (e) { toast.show(pbError(e), 'error'); }
  };

  const save = async () => {
    if (!title.trim()) { toast.show('Bitte einen Titel angeben.', 'error'); return; }
    if (!slug.trim()) { toast.show('Bitte einen Slug angeben.', 'error'); return; }
    if (!summary.trim()) { toast.show('Bitte eine Zusammenfassung angeben.', 'error'); return; }
    setSaving(true);
    try {
      const scalars: Record<string, string> = {
        title: title.trim(),
        slug: slug.trim(),
        summary: summary.trim(),
        content,
        seo_title: seoTitle,
        seo_description: seoDescription,
        seo_keyword: seoKeyword,
        category: categoryId,
        status,
      };
      // author: set on create, or backfill if missing
      if (isNew || !record?.author) scalars.author = user?.id ?? '';
      // published_at: set when publishing and not already set
      if (status === 'published' && !record?.published_at) {
        scalars.published_at = new Date().toISOString();
      }

      let body: FormData | Record<string, unknown>;
      if (file) {
        const fd = new FormData();
        Object.entries(scalars).forEach(([k, v]) => fd.append(k, v));
        fd.append('image', file);
        body = fd;
      } else {
        body = removeImage ? { ...scalars, image: null } : { ...scalars };
      }

      if (isNew) {
        const created = await pb.collection(COLLECTIONS.posts).create<Post>(body);
        toast.show('Beitrag erstellt.', 'success');
        navigate(`/admin/blog/${created.id}`, { replace: true });
      } else if (id) {
        await pb.collection(COLLECTIONS.posts).update<Post>(id, body);
        toast.show('Beitrag gespeichert.', 'success');
        navigate('/admin/blog');
      }
    } catch (e) {
      toast.show(pbError(e), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner label="Beitrag wird geladen…" />;

  const existingImageUrl = record && record.image && !removeImage ? fileUrl(record, record.image) : '';

  return (
    <div>
      <PageHeader
        title={isNew ? 'Neuer Beitrag' : 'Beitrag bearbeiten'}
        actions={<>
          <Btn variant="secondary" size="sm" onClick={() => navigate('/admin/blog')}>Zurück</Btn>
          <Btn variant="primary" size="sm" iconLeft="save" onClick={save}>{saving ? 'Speichern…' : 'Speichern'}</Btn>
        </>}
      />

      <div className="ar-split" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 22, alignItems: 'start' }}>
        {/* Main */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card>
            <TextField label="Titel" value={title} onChange={onTitle} required placeholder="Titel des Beitrags" />
            <div style={{ marginTop: 16 }}>
              <TextField label="Slug" value={slug} onChange={(v) => { setSlug(v); setSlugTouched(true); }} required
                hint={<>URL: aria-robotics.xyz/#/blog/<strong>{slug || '…'}</strong></>} />
            </div>
            <div style={{ marginTop: 16 }}>
              <label style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-100)', display: 'flex', justifyContent: 'space-between' }}>
                <span>Zusammenfassung <span style={{ color: 'var(--accent-200)' }}>*</span></span>
                <span style={{ color: summary.length >= SUMMARY_MAX ? '#e5484d' : 'var(--text-200)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{summary.length}/{SUMMARY_MAX}</span>
              </label>
              <textarea value={summary} rows={3} maxLength={SUMMARY_MAX} onChange={(e) => setSummary(e.target.value)}
                placeholder="Kurzer Teaser (max. 500 Zeichen)"
                style={{ marginTop: 6, width: '100%', boxSizing: 'border-box', fontFamily: 'var(--font-sans)', fontSize: 15, color: 'var(--text-100)', background: 'var(--surface-page)', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', resize: 'vertical', outline: 'none' }} />
            </div>
          </Card>

          <Card>
            <label style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-100)', display: 'block', marginBottom: 8 }}>Blogbeitrag</label>
            <RichTextEditor value={content} onChange={setContent} />
          </Card>

          <Card>
            <label style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-100)', display: 'block', marginBottom: 8 }}>Titelbild</label>
            <ImageDropzone
              file={file}
              existingUrl={existingImageUrl}
              onFile={(f) => { setFile(f); if (f) setRemoveImage(false); }}
              onClearExisting={() => { setRemoveImage(true); setFile(null); }}
            />
          </Card>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card>
            <SelectField label="Status" value={status} onChange={(v) => setStatus(v as PostStatus)}
              options={[{ value: 'draft', label: 'Entwurf' }, { value: 'published', label: 'Veröffentlicht' }]} />
            <div style={{ marginTop: 16 }}>
              <label style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-100)', display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span>Kategorie</span>
                <button type="button" onClick={addCategory} style={{ background: 'none', border: 'none', color: 'var(--primary-200)', fontSize: 12.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Icon name="plus" size={13} /> Neu</button>
              </label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', fontSize: 15, color: 'var(--text-100)', background: 'var(--surface-page)', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', cursor: 'pointer' }}>
                <option value="">— keine —</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </Card>

          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-100)', marginBottom: 14 }}>SEO</div>
            <SeoField label="SEO-Titel" value={seoTitle} onChange={setSeoTitle} rec={SEO_TITLE_REC} placeholder={title} />
            <div style={{ marginTop: 14 }}>
              <SeoField label="SEO-Beschreibung" value={seoDescription} onChange={setSeoDescription} rec={SEO_DESC_REC} textarea placeholder={summary} />
            </div>
            <div style={{ marginTop: 14 }}>
              <TextField label="SEO-Keyword" value={seoKeyword} onChange={setSeoKeyword} placeholder="Fokus-Keyword" />
            </div>
            <div style={{ marginTop: 16 }}>
              <SerpPreview slug={slug} title={title} seoTitle={seoTitle} seoDescription={seoDescription} summary={summary} />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function SeoField({ label, value, onChange, rec, textarea, placeholder }: { label: string; value: string; onChange: (v: string) => void; rec: number; textarea?: boolean; placeholder?: string }) {
  const over = value.length > rec;
  return (
    <div>
      <label style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-100)', display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span>{label}</span>
        <span style={{ color: over ? '#e5883a' : 'var(--text-200)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{value.length}/{rec}</span>
      </label>
      {textarea ? (
        <textarea value={value} rows={3} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'var(--font-sans)', fontSize: 14.5, color: 'var(--text-100)', background: 'var(--surface-page)', padding: '9px 12px', borderRadius: 'var(--radius-sm)', border: `1px solid ${over ? '#e5883a' : 'var(--border-default)'}`, resize: 'vertical', outline: 'none' }} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          style={{ width: '100%', boxSizing: 'border-box', fontSize: 14.5, color: 'var(--text-100)', background: 'var(--surface-page)', padding: '9px 12px', borderRadius: 'var(--radius-sm)', border: `1px solid ${over ? '#e5883a' : 'var(--border-default)'}`, outline: 'none' }} />
      )}
    </div>
  );
}
